import { TEXT_ROLES } from "../text/typography"

// Logical pixels; these tiers describe layout density, independently of device input capability.
export const UI_SPACING = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24 } as const
export const UI_RADII = { control: 5, card: 8, panel: 10 } as const
// Sizes own the label/subtitle hierarchy as well as the visible control dimensions.
const buttonSize = (height: number, paddingX: number, labelRole: keyof typeof TEXT_ROLES,
    subtitleRole: keyof typeof TEXT_ROLES) => ({ height, paddingX, labelRole, subtitleRole,
    fontSize: TEXT_ROLES[labelRole].fontSize, subtitleFontSize: TEXT_ROLES[subtitleRole].fontSize })
const SMALL_BUTTON = buttonSize(24, UI_SPACING.sm, "compactBody", "overline")
const MEDIUM_BUTTON = buttonSize(32, UI_SPACING.md, "label", "compactCaption")
export const BUTTON_SIZES = {
    extraSmall: buttonSize(16, UI_SPACING.xs, "overline", "overline"),
    small: SMALL_BUTTON,
    medium: MEDIUM_BUTTON,
    large: buttonSize(40, UI_SPACING.lg, "body", "caption"),
    extraLarge: buttonSize(48, UI_SPACING.xl, "title", "label"),
    // Compatibility aliases for existing integrations; new callers use the names above.
    compact: SMALL_BUTTON,
    regular: MEDIUM_BUTTON,
} as const
export type ButtonSize = keyof typeof BUTTON_SIZES

/**
 * THE HUD PANEL: the dark plate every floating readout and control in the HUD is drawn on.
 *
 * It lives here because nine widgets were each declaring their own copy of it -- the chip row, the
 * players chip and its panel, the minimap, the invite prompt, the anxiety meter, the follow-cam
 * frame -- and they had all drifted to a cool near-black (0x05070a) with blue-grey labels and WHITE
 * rims, while everything else in the HUD moved to timber and bronze: the readied-item tray
 * (0x1b1512 / 0x6b4f38), the countdown dial and ember gauge (build_board's TIMBER / RIM), the roster
 * cards. The result was three corners of warm wood and one of slate, which read as two different
 * games sharing a screen.
 *
 * These are the tray's own colours, because the tray is the thing most of them sit nearest to.
 * A widget wanting a DIFFERENT look should still take these and tint from them, so the family
 * cannot come apart again.
 */
export const HUD_PANEL_FILL = 0x1b1512
export const HUD_PANEL_RIM = 0x8e7b58
export const HUD_PANEL_TEXT = 0xfff3e0
// Resting label colour: the panel text, stepped back so hover and selection have somewhere to go.
export const HUD_PANEL_TEXT_MUTED = 0xcbb49a

export const UI_THEMES = {
    slate: {
        text: 0xe6e9f0, onAccent: 0x1b2030, fill: 0x2f3648, hoverFill: 0x3f4a63, pressedFill: 0x232a3d,
        border: 0x3a4256, accent: 0xd9a441, success: 0x7be08a, danger: 0xd98a8a,
    },
    ember: {
        text: 0xfff3e0, onAccent: 0x2a1a0c, fill: 0x2a201a, hoverFill: 0x3a2c21, pressedFill: 0x1b1512,
        border: 0x6b4f38, accent: 0xf2a94e, success: 0x7be08a, danger: 0xe8745a,
    },
} as const
export type UiTheme = keyof typeof UI_THEMES
export type ButtonVariant = "secondary" | "primary" | "success" | "danger"
export const BUTTON_DISABLED_ALPHA = 0.45

export type ButtonAppearance = "filled" | "outline" | "subtle"
export type ButtonShape = "rounded" | "pill" | "circle"
